import React from "react";
import { StyleSheet, View } from "react-native";
import { useSelector } from "react-redux";
import { FormProvider, useFieldArray, useForm } from "react-hook-form";

import { PdButton, PdButtonLabel } from "src/components/forms/button";
import { PdModal, PdModalCloseButton, PdModalTitle } from "src/components/overlay";
import { tUseModal, useModal } from "src/providers/OverlayProvider";
import { kColors } from "src/kColors";
import { kStyleConst } from "src/kStyle";
import { tModalComponentProps } from "src/redux/modals/types";
import { PdStack } from "src/components/layout";
import { PdFormLabel } from "src/components/form-control";
import { PdHookFormErrorMessage, PdHookFormSelect } from "src/components/hook-form";
import { queryBrands } from "src/db/brand.db";
import { queryRetailers } from "src/db/retailer.db";
import { tPdSelectOption } from "src/components/forms/select";
import { PdIconButton } from "src/components/forms/icon-button";
import { PdTrash } from "src/components/Icons";
import { connectCustomers } from "src/redux/Admin/actions";
import { useAppDispatch } from "src/redux";
import { tConnectionSource, tPostConnectionsRequestBody } from "tmt_api/src/types/types";
import { getApiError } from "src/util/apiNew";

type tOptionFormData<T extends unknown = string> = { label: string; value: T } | null;

type tConnectCustomersFormData = {
  connections: { brand: tOptionFormData; retailer: tOptionFormData; source: tOptionFormData<tConnectionSource> }[];
};

const sourceOptions: tPdSelectOption[] = [
  { value: "The Main Tab", label: "The Main Tab" },
  { value: "Direct", label: "Direct" },
];

export const AdminConnectCustomers: React.FC<tModalComponentProps> = ({ onClose, onResolve }) => {
  const dispatch = useAppDispatch();
  const rIsMobile = useSelector(
    (state) => state.NavigationReducer.deviceSize === "xs" || state.NavigationReducer.deviceSize === "sm",
  );
  const formMethods = useForm<tConnectCustomersFormData>({
    defaultValues: {
      connections: [{ brand: null, retailer: null, source: null }],
    },
  });
  const { fields, append, remove } = useFieldArray({
    control: formMethods.control,
    name: "connections",
  });
  const [brandOptions, setBrandOptions] = React.useState<tPdSelectOption[]>([]);
  const [retailerOptions, setRetailerOptions] = React.useState<tPdSelectOption[]>([]);
  const [isSubmitting, setSubmitting] = React.useState(false);

  const handleOnClose = () => {
    onClose();
  };

  const handleOnConnect = formMethods.handleSubmit(async (formData) => {
    try {
      setSubmitting(true);
      const payload = formData.connections.map<tPostConnectionsRequestBody[0]>((connection) => ({
        brandId: connection.brand?.value ?? "",
        retailerId: connection.retailer?.value ?? "",
        source: connection.source?.value ?? "Direct",
      }));
      await dispatch(connectCustomers(payload));

      dispatch({ type: "RIBBON", message: "Connections updated" });
      onResolve();
    } catch (e) {
      setSubmitting(false);
      dispatch({ type: "RIBBON", message: new Error(getApiError(e).message) });
    }
  });

  React.useEffect(() => {
    (async () => {
      const brandsQuery = await queryBrands().get();
      const retailersQuery = await queryRetailers().get();

      setBrandOptions(
        brandsQuery.docs
          .map((doc) => {
            const brand = doc.data();
            return { value: brand.id, label: brand.name };
          })
          .sort((b1, b2) => b1.label.localeCompare(b2.label)),
      );
      setRetailerOptions(
        retailersQuery.docs
          .map((doc) => {
            const retailer = doc.data();
            return { value: retailer.id, label: retailer.name };
          })
          .sort((r1, r2) => r1.label.localeCompare(r2.label)),
      );
    })();
  }, []);

  return (
    <PdModal closeOnOverlayClick={!isSubmitting} style={styles.confirmModal}>
      <PdModalCloseButton isDisabled={isSubmitting} onPress={handleOnClose} />
      <View>
        <PdModalTitle style={styles.confirmModalTitle}>Connect Customers</PdModalTitle>
        <FormProvider {...formMethods}>
          {fields.map((field, index) => (
            <PdStack
              key={field.id}
              layout={[1, 1, 1]}
              direction={rIsMobile ? "vertical" : "horizontal"}
              style={[rIsMobile ? styles.fieldRowMobile : styles.fieldRowWeb, { zIndex: fields.length - index }]}
            >
              <View style={{ zIndex: fields.length - index + 3 }}>
                <PdFormLabel>Brand *</PdFormLabel>
                <PdHookFormSelect
                  name={`connections.${index}.brand`}
                  rules={{ required: "Required" }}
                  placeholder="Select a Brand"
                  options={brandOptions}
                  isClearable={false}
                />
                <PdHookFormErrorMessage name={`connections.${index}.brand`} />
              </View>
              <View style={{ zIndex: fields.length - index + 2 }}>
                <PdFormLabel>Retailer *</PdFormLabel>
                <PdHookFormSelect
                  name={`connections.${index}.retailer`}
                  rules={{ required: "Required" }}
                  placeholder="Select a Retailer"
                  options={retailerOptions}
                  isClearable={false}
                />
                <PdHookFormErrorMessage name={`connections.${index}.retailer`} />
              </View>
              <View style={{ zIndex: fields.length - index + 1 }}>
                <PdFormLabel>Source *</PdFormLabel>
                <PdHookFormSelect
                  name={`connections.${index}.source`}
                  rules={{ required: "Required" }}
                  placeholder="Select a Source"
                  options={sourceOptions}
                  isClearable={false}
                />
                <PdHookFormErrorMessage name={`connections.${index}.source`} />
              </View>
              <PdIconButton
                colorScheme="red"
                contentContainerStyle={styles.removeButton}
                onPress={() => remove(index)}
                icon={<PdTrash style={styles.removeButtonIcon} />}
                isDisabled={fields.length < 2}
              />
            </PdStack>
          ))}
          <PdButton
            colorScheme="pink"
            style={styles.addFieldRowButton}
            onPress={() => append({ brand: null, retailer: null })}
          >
            <PdButtonLabel>Add Connection</PdButtonLabel>
          </PdButton>
        </FormProvider>
        <PdStack direction={rIsMobile ? "vertical" : "horizontal"} layout={[1, 1]} isReversed={rIsMobile}>
          <PdButton isDisabled={isSubmitting} onPress={handleOnClose} variant="outline">
            <PdButtonLabel>Cancel</PdButtonLabel>
          </PdButton>
          <PdButton
            isLoading={isSubmitting}
            onPress={handleOnConnect}
            style={rIsMobile ? { marginBottom: 2 * kStyleConst.kSpacing } : {}}
          >
            <PdButtonLabel>Connect</PdButtonLabel>
          </PdButton>
        </PdStack>
      </View>
    </PdModal>
  );
};

const useAdminConnectCustomers = (): tUseModal => {
  return useModal(AdminConnectCustomers);
};

export default useAdminConnectCustomers;

const styles = StyleSheet.create({
  addFieldRowButton: {
    marginBottom: 4 * kStyleConst.kSpacing,
    marginTop: 2 * kStyleConst.kSpacing,
  },
  confirmModal: {
    backgroundColor: kColors.white,
    maxWidth: 600,
    padding: 6 * kStyleConst.kSpacing,
    width: "100%",
  },
  confirmModalTitle: {
    marginBottom: 4 * kStyleConst.kSpacing,
  },
  fieldRowMobile: {
    marginBottom: 2 * kStyleConst.kSpacing,
  },
  fieldRowWeb: {
    alignItems: "flex-end",
    marginBottom: 2 * kStyleConst.kSpacing,
  },
  removeButton: {
    height: 37,
    width: 37,
  },
  removeButtonIcon: {
    height: 15,
    width: 15,
  },
});
